import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Upload, FileText, CheckCircle, AlertTriangle, Loader2, ArrowLeft, LayoutDashboard, LogOut, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function getAuthHeader() {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminImport() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [log, setLog] = useState([]);
  const [summary, setSummary] = useState(null);
  const [polling, setPolling] = useState(false);
  const logEndRef = useRef(null);

  // Poll import status
  const pollStatus = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/api/import/status`, { headers: getAuthHeader() });
      const data = resp.data;
      setLog(data.log || []);
      if (data.done) {
        setSummary(data.summary);
        setImporting(false);
        setPolling(false);
        if (data.summary?.error) {
          toast.error('Error en la importacion');
        } else {
          toast.success('Importacion completada');
        }
      }
    } catch (err) {
      console.error('Poll error:', err);
    }
  }, []);

  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(pollStatus, 2000);
    return () => clearInterval(interval);
  }, [polling, pollStatus]);

  // Auto-scroll log
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [log]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f && (f.name.endsWith('.xml') || f.type === 'text/xml' || f.type === 'application/xml')) {
      setFile(f);
      setLog([]);
      setSummary(null);
    } else {
      toast.error('Por favor selecciona un archivo XML valido');
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setLog(['Subiendo archivo...']);
    setSummary(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/api/import/wordpress`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });

      // Start polling
      setPolling(true);
      setLog(prev => [...prev, 'Archivo subido, procesando en segundo plano...']);
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      setLog(prev => [...prev, `Error: ${msg}`]);
      toast.error(`Error: ${msg}`);
      setImporting(false);
    }
  };

  const handleReset = async () => {
    try {
      await axios.post(`${API}/api/import/reset`, {}, { headers: getAuthHeader() });
      setImporting(false);
      setPolling(false);
      setLog([]);
      setSummary(null);
      toast.success('Estado reiniciado');
    } catch (err) {
      toast.error('Error al reiniciar');
    }
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-import">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 bg-card border-r border-border flex-col">
        <div className="p-6 border-b border-border">
          <Link to="/"><img src="/logo.png" alt="Chocados GDL" className="h-10" /></Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
            <LayoutDashboard className="w-5 h-5" /><span className="font-medium">Dashboard</span>
          </Link>
          <div className="flex items-center gap-3 px-4 py-3 rounded bg-primary/10 text-primary">
            <Upload className="w-5 h-5" /><span className="font-medium">Importar WordPress</span>
          </div>
        </nav>
        <div className="p-4 border-t border-border">
          <Button variant="ghost" className="w-full justify-start text-muted-foreground" onClick={() => { logout(); navigate('/'); }}>
            <LogOut className="w-5 h-5 mr-3" />Cerrar Sesion
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-8 overflow-auto">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/admin" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></Link>
            <div>
              <h1 className="text-2xl font-bold uppercase tracking-wider">Importar desde WordPress</h1>
              <p className="text-sm text-muted-foreground mt-1">Sube el archivo XML exportado de WordPress para migrar los vehiculos</p>
            </div>
          </div>

          {/* Upload */}
          <div
            className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer mb-6"
            onClick={() => !importing && fileRef.current?.click()}
            data-testid="upload-area"
          >
            <input ref={fileRef} type="file" accept=".xml" onChange={handleFileChange} className="hidden" />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-8 h-8 text-primary" />
                <div className="text-left">
                  <p className="font-medium text-foreground">{file.name}</p>
                  <p className="text-sm text-muted-foreground">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
                </div>
              </div>
            ) : (
              <div>
                <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">Haz clic para seleccionar el archivo XML de WordPress</p>
                <p className="text-xs text-muted-foreground mt-1">Formato: WordPress eXtended RSS (.xml)</p>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 mb-6">
            <Button onClick={handleImport} disabled={!file || importing} className="flex-1 btn-primary h-12 text-base" data-testid="import-btn">
              {importing ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Importando...</>
              ) : (
                <><Upload className="w-5 h-5 mr-2" />Iniciar Importacion</>
              )}
            </Button>
            {(importing || log.length > 0) && (
              <Button onClick={handleReset} variant="outline" className="h-12" data-testid="reset-btn" title="Reiniciar estado">
                <RotateCcw className="w-5 h-5" />
              </Button>
            )}
          </div>

          {/* Summary */}
          {summary && !summary.error && (
            <div className="bg-card border border-border rounded-lg p-6 mb-6" data-testid="import-summary">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <h2 className="font-bold text-lg">Resumen de Importacion</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { val: summary.importados, label: 'Importados', color: 'text-primary' },
                  { val: summary.omitidos, label: 'Omitidos', color: 'text-foreground' },
                  { val: summary.errores, label: 'Errores', color: 'text-destructive' },
                  { val: summary.total_en_xml, label: 'Total en XML', color: 'text-foreground' },
                  { val: summary.adjuntos_encontrados, label: 'Adjuntos', color: 'text-foreground' },
                  { val: summary.imagenes_descargadas, label: 'Imgs descargadas', color: 'text-green-500' },
                  { val: summary.imagenes_fallidas, label: 'Imgs fallidas', color: 'text-yellow-500' },
                  { val: summary.categorias_creadas, label: 'Categorias nuevas', color: 'text-foreground' },
                ].map((item, i) => (
                  <div key={i} className="bg-muted/50 rounded p-3">
                    <p className={`text-2xl font-bold ${item.color}`}>{item.val ?? 0}</p>
                    <p className="text-xs text-muted-foreground">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {summary?.error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-6 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                <h2 className="font-bold text-lg text-destructive">Error en la Importacion</h2>
              </div>
              <p className="text-sm text-muted-foreground">{summary.error}</p>
            </div>
          )}

          {/* Log */}
          {log.length > 0 && (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
                <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Log de Importacion</h3>
                {importing && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
              </div>
              <div className="p-4 max-h-80 overflow-y-auto font-mono text-xs space-y-1" data-testid="import-log">
                {log.map((entry, i) => (
                  <div key={i} className={entry.includes('Error') ? 'text-destructive' : entry.includes('COMPLETADO') ? 'text-green-500 font-bold' : 'text-muted-foreground'}>
                    {entry}
                  </div>
                ))}
                <div ref={logEndRef} />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
